import { Card, FormControlLabel, makeStyles, Switch } from "@material-ui/core";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Grid from "@saleor/components/Grid";
// import Metadata from "@saleor/components/Metadata";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
// import SeoForm from "@saleor/components/SeoForm";
import { ProductErrorWithAttributesFragment } from "@saleor/fragments/types/ProductErrorWithAttributesFragment";
import { TaxTypeFragment } from "@saleor/fragments/types/TaxTypeFragment";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import ProductKAMSDropdownGroup from "@saleor/products/components/ProductKAMSDropdownGroup";
import { Brand } from "@saleor/products/customTypes/Brand";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import useSetBrandOnLoad from "@saleor/products/hooks/useSetBrandOnLoad";
import { getChoices } from "@saleor/products/utils/data";
import { SearchCategories_search_edges_node } from "@saleor/searches/types/SearchCategories";
import { SearchCollections_search_edges_node } from "@saleor/searches/types/SearchCollections";
import { SearchProductTags_search_edges_node } from "@saleor/searches/types/SearchProductTags";
import { SearchProductTypes_search_edges_node } from "@saleor/searches/types/SearchProductTypes";
import { SearchWarehouses_search_edges_node } from "@saleor/searches/types/SearchWarehouses";
import { hasFullAccess } from "@saleor/utils/smop/checkSuperAdmin";
import { ContentState, convertToRaw } from "draft-js";
import React, { useEffect } from "react";

import { FetchMoreProps } from "../../../types";
import ProductAttributes from "../ProductAttributes";
import ProductDetailsForm from "../ProductDetailsForm";
import ProductDimension from "../ProductDimensions";
import ProductOrganization from "../ProductOrganization";
import ProductPricing from "../ProductPricing";
import ProductShipping from "../ProductShipping/ProductShipping";
import ProductStatus from "../ProductStatus";
import ProductStocks from "../ProductStocks";
import ProductTaxes from "../ProductTaxes";
import ProductCreateForm, {
  ProductCreateData,
  ProductCreateFormData
} from "./form";

const useStyles = makeStyles(
  {
    variantToggle: {
      display: "grid"
    }
  },
  { name: "ProductCreatePage" }
);
interface ProductCreatePageProps {
  brand?: string;
  brands?: Brand[];
  brandLabel?: string;
  onBrandChange?: (e) => void;
  userView?: string;
  userViews?: any;
  userViewLabel?: string;
  onUserViewChange?: (e) => void;
  errors: ProductErrorWithAttributesFragment[];
  collections: SearchCollections_search_edges_node[];
  categories: SearchCategories_search_edges_node[];
  currency: string;
  disabled: boolean;
  tags: SearchProductTags_search_edges_node[];
  fetchMoreTags: FetchMoreProps;
  fetchMoreCategories: FetchMoreProps;
  fetchMoreCollections: FetchMoreProps;
  fetchMoreProductTypes: FetchMoreProps;
  initial?: Partial<ProductCreateFormData>;
  productTypes?: SearchProductTypes_search_edges_node[];
  header: string;
  saveButtonBarState: ConfirmButtonTransitionState;
  weightUnit: string;
  warehouses: SearchWarehouses_search_edges_node[];
  taxTypes: TaxTypeFragment[];
  isTrackInventory?: boolean;
  productsUser: ProductsUser;
  validation: {
    basePrice: null;
    category: null;
    description: null;
    height: null;
    length: null;
    name: null;
    sku: null;
    productStatus: null;
    weight: null;
    width: null;
  };
  fetchCategories: (data: string) => void;
  fetchCollections: (data: string) => void;
  fetchProductTypes: (data: string) => void;
  onWarehouseConfigure: () => void;
  fetchTags: (query: string) => void;
  onBack?();
  onSubmit?(data: ProductCreateData);
}

export const ProductCreatePage: React.FC<ProductCreatePageProps> = ({
  currency,
  disabled,
  categories: categoryChoiceList,
  collections: collectionChoiceList,
  errors,
  fetchCategories,
  fetchCollections,
  fetchMoreCategories,
  fetchMoreCollections,
  fetchMoreProductTypes,
  fetchMoreTags,
  fetchTags,
  tags,
  header,
  initial,
  productTypes: productTypeChoiceList,
  saveButtonBarState,
  warehouses,
  taxTypes,
  isTrackInventory,
  onBack,
  fetchProductTypes,
  weightUnit,
  onSubmit,
  onWarehouseConfigure,
  brand,
  brands,
  brandLabel,
  onBrandChange,
  userView,
  userViews,
  userViewLabel,
  onUserViewChange,
  productsUser,
  validation
}: ProductCreatePageProps) => {
  const classes = useStyles({});

  // Ensures that it will not change after component rerenders, because it
  // generates different block keys and it causes editor to lose its content.
  const initialDescription = React.useRef(
    convertToRaw(ContentState.createFromText(""))
  );

  // Display values
  const [selectedCategory, setSelectedCategory] = useStateFromProps(
    initial?.category || ""
  );

  const [selectedCollections, setSelectedCollections] = useStateFromProps<
    MultiAutocompleteChoiceType[]
  >([]);

  const [selectedTaxType, setSelectedTaxType] = useStateFromProps(
    initial?.taxCode || null
  );
  const categories = getChoices(categoryChoiceList);
  const collections = getChoices(collectionChoiceList);
  const productTypes = getChoices(productTypeChoiceList);
  const tagsChoices = getChoices(tags);
  const taxTypeChoices =
    taxTypes?.map(taxType => ({
      label: taxType.description,
      value: taxType.taxCode
    })) || [];

  const breadcrumbDirectory = [
    {
      name: "Products",
      url: "/products"
    },
    {
      name: "New Product"
    }
  ];

  return (
    <ProductCreateForm
      onSubmit={onSubmit}
      initial={initial}
      categories={categories}
      collections={collections}
      productTypes={productTypeChoiceList}
      selectedCollections={selectedCollections}
      setSelectedCategory={setSelectedCategory}
      setSelectedCollections={setSelectedCollections}
      setSelectedTaxType={setSelectedTaxType}
      isTrackInventory={isTrackInventory}
      taxTypes={taxTypeChoices}
      warehouses={warehouses}
    >
      {({ change, data, handlers, hasChanged, submit }) => {
        // Comparing explicitly to false because `hasVariants` can be undefined
        const isSimpleProduct = data.productType?.hasVariants === false;
        const hasAccess = hasFullAccess();

        useSetBrandOnLoad(change, brand);

        useEffect(() => {
          if (!hasAccess && productTypes.length > 0) {
            handlers.selectProductType({
              target: {
                name: "productType",
                value: productTypes[0].value
              }
            });
          }
        }, [productTypes]);

        return (
          <Container>
            {productsUser.isKAMS && (
              <ProductKAMSDropdownGroup
                brand={brand}
                brandChoices={brands}
                brandLabel={brandLabel}
                onBrandChange={onBrandChange}
                userView={userView}
                userViewChoices={userViews}
                userViewLabel={userViewLabel}
                onUserViewChange={onUserViewChange}
              />
            )}
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={header} />
            </div>
            <Grid>
              <div>
                <ProductDetailsForm
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  fetchTags={fetchTags}
                  fetchMoreTags={fetchMoreTags}
                  initialDescription={initialDescription.current}
                  onChange={change}
                  tags={tagsChoices}
                  validate={validation}
                />
                <CardSpacer />
                <CardSpacer />
                {data.attributes.length > 0 && (
                  <ProductAttributes
                    attributes={data.attributes}
                    disabled={disabled}
                    errors={errors}
                    onChange={handlers.selectAttribute}
                    onMultiChange={handlers.selectAttributeMultiple}
                  />
                )}
                <CardSpacer />
                <Card>
                  <CardTitle
                    className={classes.variantToggle}
                    title={""}
                    toolbar={
                      <FormControlLabel
                        control={
                          <Switch
                            id="doesProductHaveVariantsToggle"
                            name="isSimpleProduct"
                            checked={!data.isSimpleProduct}
                            onChange={e =>
                              change({
                                target: {
                                  name: e.target.name,
                                  value: !e.target.checked
                                }
                              })
                            }
                            color="primary"
                          />
                        }
                        label="Product has variants"
                      />
                    }
                  />
                </Card>
                <CardSpacer />
                {data.isSimpleProduct && (
                  <>
                    <ProductPricing
                      currency={currency}
                      data={data}
                      disabled={disabled}
                      errors={errors}
                      onChange={change}
                      validate={validation}
                    />
                    <CardSpacer />
                    <ProductDimension
                      data={data}
                      onFormDataChange={change}
                      title="Dimension"
                      isStoreAdmin={productsUser.isStoreAdmin}
                      validate={validation}
                    />
                    <CardSpacer />
                    <ProductStocks
                      data={data}
                      disabled={disabled}
                      hasVariants={false}
                      onFormDataChange={change}
                      errors={errors}
                      stocks={data.stocks}
                      warehouses={warehouses}
                      onChange={handlers.changeStock}
                      onWarehouseStockAdd={handlers.addStock}
                      onWarehouseStockDelete={handlers.deleteStock}
                      onWarehouseConfigure={onWarehouseConfigure}
                      mall={null}
                      validate={validation}
                    />
                  </>
                )}
                <CardSpacer />
                {isSimpleProduct && ( // Unreachable code since all product types have variants
                  <>
                    <ProductShipping
                      data={data}
                      disabled={disabled}
                      errors={errors}
                      weightUnit={weightUnit}
                      onChange={change}
                    />
                    <CardSpacer />
                    <ProductStocks
                      data={data}
                      disabled={disabled}
                      hasVariants={false}
                      onFormDataChange={change}
                      errors={errors}
                      stocks={data.stocks}
                      warehouses={warehouses}
                      onChange={handlers.changeStock}
                      onWarehouseStockAdd={handlers.addStock}
                      onWarehouseStockDelete={handlers.deleteStock}
                      onWarehouseConfigure={onWarehouseConfigure}
                      mall={null}
                      validate={validation}
                    />
                    <CardSpacer />
                  </>
                )}
                <CardSpacer />
              </div>
              <div>
                <ProductStatus
                  data={data}
                  disabledOptions={["ready", "unlisted"]}
                  onChange={change}
                />
                <CardSpacer />
                <ProductOrganization
                  canChangeCategory={productsUser.isSellerMaster}
                  canChangeType={true}
                  categories={categories}
                  categoryInputDisplayValue={selectedCategory}
                  collections={collections}
                  data={data}
                  disabled={disabled}
                  disabledCollection={productsUser.isSellerMaster}
                  errors={errors}
                  fetchCategories={fetchCategories}
                  fetchCollections={fetchCollections}
                  fetchMoreCategories={fetchMoreCategories}
                  fetchMoreCollections={fetchMoreCollections}
                  fetchMoreProductTypes={fetchMoreProductTypes}
                  fetchProductTypes={fetchProductTypes}
                  productType={data.productType}
                  productTypeInputDisplayValue={data.productType?.name || ""}
                  productTypes={productTypes}
                  onCategoryChange={handlers.selectCategory}
                  onCollectionChange={handlers.selectCollection}
                  onProductTypeChange={handlers.selectProductType}
                  collectionsInputDisplayValue={selectedCollections}
                  validate={validation}
                />
                <CardSpacer />
                <ProductTaxes
                  data={data}
                  disabled={disabled}
                  onChange={change}
                  onTaxTypeChange={handlers.selectTaxRate}
                  selectedTaxTypeDisplayName={selectedTaxType}
                  taxTypes={taxTypes}
                />
              </div>
            </Grid>
            <SaveButtonBar
              id="product"
              onCancel={onBack}
              onSave={submit}
              state={saveButtonBarState}
              disabled={disabled || !onSubmit || !hasChanged}
            />
          </Container>
        );
      }}
    </ProductCreateForm>
  );
};
ProductCreatePage.displayName = "ProductCreatePage";
export default ProductCreatePage;
